import type { ActivityLog as ActivityLogEntry } from 'types';
import { formatDateTime } from 'helpers/formatters';
import { UserAvatar } from '@/components/UserAvatar';
import { userFullName } from 'helpers/user-identity';
import { useExperiment } from '@/state/experiments/useExperiment';
import { activityActorMarker } from './activity-actor-marker';

type ActivityLogProps = { experimentId: number };

/**
 * An activity feed is a log, so it is typeset as one: mono log lines, one row per entry,
 * timestamp right-aligned. No separators — the row hover is the only structure needed.
 */
export const ActivityLog = ({ experimentId }: ActivityLogProps) => {
  const { experiment } = useExperiment(experimentId);

  if (!experiment) return null;

  const { activityLog } = experiment;

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="settings-activity">
      <div className="border-line border-b px-4 py-3">
        <h2 className="micro-label text-foreground">Activity log</h2>
        <p className="supporting mt-1">A record of recent actions and changes within this experiment.</p>
      </div>

      <ActivityEntries entries={activityLog ?? []} />
    </section>
  );
};

type ActivityEntriesProps = {
  entries: readonly Pick<ActivityLogEntry, 'id' | 'user' | 'userId' | 'actorType' | 'message' | 'createdAt'>[];
};

/** The log's rows, shared with the feature flag page, whose activity comes with the flag. */
export const ActivityEntries = ({ entries }: ActivityEntriesProps) => (
  <div className="p-2">
    {entries.length > 0 ? (
      entries.map((entry) => (
        <div key={entry.id} className="hover:bg-hover rounded-card flex items-start justify-between gap-3 px-2 py-1.5">
          <span className="log-line flex min-w-0 items-center gap-2">
            {/* The badge took the place of the accent dot. The first name stays
                    written out: it is the subject of the entry, and a log is read
                    down the page, not hovered row by row. */}
            {entry.user ? (
              <>
                <UserAvatar user={entry.user} size="xs" />
                <span className="text-foreground shrink-0 truncate" title={userFullName(entry.user)}>
                  {entry.user.firstName || userFullName(entry.user)}
                </span>
              </>
            ) : (
              <span className="bg-accent size-[5px] shrink-0 rounded-pill" aria-hidden="true" />
            )}
            {/* Plain text, so it reads as part of the log line, not a badge. */}
            {activityActorMarker(entry) && <span className="micro-label shrink-0">{activityActorMarker(entry)}</span>}
            {/* The message IS the log entry, so a clipped one is an entry the
                    reader cannot read at all. */}
            <span className="text-dim truncate" title={entry.message ?? undefined}>
              {entry.message}
            </span>
          </span>
          <span className="log-line text-dim shrink-0">{formatDateTime(entry.createdAt ?? 0)}</span>
        </div>
      ))
    ) : (
      <p className="supporting px-2 py-1.5">No activity recorded yet.</p>
    )}
  </div>
);
